import React, { useState, useEffect, FunctionComponent, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../common/contexts/UserContext";
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext";
import ComponentWrapper from "../../common/components/ComponentWrapper";
import Button from "@murphy-frontend/web-core/components/Button";
import MBSMessageEditor from "../MBS/MBSCreateNew/MBSMessageEditor";
import MBSTemplateSelector from "../MBS/MBSCreateNew/MBSTemplateSelector";
import Spinner from "@murphy-frontend/web-core/components/Spinner";
import ContactList, { ContactListModel } from "./../Contacts/components/ContactList";
import EditContact from "./../Contacts/components/EditContact";
import MBSConfirmation from "../MBS/MBSCreateNew/MBSConfirmation";
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries";
import queryKeys from "./apiAlert/queryKeys";
import { useGetContacts, useGetCustomerContactOptionSets } from "../../common/api/Contacts/queries";
import { useGetContactGroups } from "@murphy-frontend/common/api/ContactGroups/Queries";
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext";
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal";
import { CreateSendOutBatchRequest, Message } from "../MBS/MBSApi";
import { SendOutBatchTemplate, CustomAlertResponse } from "../MBS/models";
import {
    ContactModel,
    OptionSetModel,
    OptionSetViewModel,
    SelectedOptionSetViewModel
} from "../../common/api/Contacts/ContactsApi";
import { AlertTemplateDTO, MBSGroupMember, MBSRecipient } from "./apiAlert/models";
import { useCustomer } from "../../common/contexts/CustomerContext";
import MBSSearchRecipients from "../MBS/MBSCreateNew/MBSSearchRecipients";
import MBSSelectedRecipients from "../MBS/MBSCreateNew/MBSSelectedRecipients";
import CommunicationTypeFilter from "@murphy-frontend/web-core/components/CommunicationTypeFilter";
import { MurphyUser } from "@murphy-frontend/common/interfaces/IUsersApi";
import { ContactGroupModel } from "@murphy-frontend/common/interfaces/IContactGroupApi";
import { Roles } from "@murphy-frontend/common/enums";
import { CustomCheckbox } from "../../common/components/MurphyCheckbox";
import InfoIcon from "@murphy-frontend/web-core/components/InfoIcon";
import RichTextDisplay from "@murphy-frontend/web-core/features/RichText/RichTextDisplay";
import { parseHtml } from "@murphy-frontend/web-core/features/RichText/helpers";
import { enabledFeatures } from "../../common/variables";
import TabPanel from "@murphy-frontend/web-core/components/Tabs/TabPanel";
import { useGetAlertTemplates, useGetAlertTemplate } from './apiAlert/queries';
import AlertTemplateBasicInfo from './components/AlertTemplateBasicInfo';
import ChannelSelection from "../MBS/MBSCreateNew/ChannelSelection";
import MBSChooseRecipients from "../MBS/MBSCreateNew/MBSChooseRecipients";
import { useCreateAlertTemplate, useUpdateAlertTemplate } from "./apiAlert/mutations"; // Assuming the file with mutations is imported correctly
import { CreateAlertTemplateDTO, UpdateAlertTemplateDTO, AlertTemplateRecipientDTO } from "./apiAlert/models";

const CreateAlertTemplate: FunctionComponent = () => {
    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();
    const { translations } = useUserPreferences();
    const { user } = useUser();
    const [searchParams] = useSearchParams();
    const templateIdParam = searchParams.get("templateId");
    const templateId = templateIdParam ? parseInt(templateIdParam, 10) : null;
    const [selectedTabId, setSelectedTabId] = useState<number>(1);
    const { mutate: createAlertTemplate } = useCreateAlertTemplate();
    const { mutate: updateAlertTemplate } = useUpdateAlertTemplate();

    const hasInitialized = useRef(false);
    const isInitialLoad = useRef(true);   // Guards change detection
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [users, setUsers] = useState<MBSRecipient[]>([]);
    const [contacts, setContacts] = useState<MBSRecipient[]>([]);
    const [contactGroups, setContactGroups] = useState<MBSRecipient[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [stepsEnabled, setStepsEnabled] = useState<boolean>(false);

    const [messageTemplateIsFlashing, setMessageTemplateIsFlashing] = useState<boolean>(false);
    const [EnableEventflowIsChecked, setEnableEventflow] = useState<boolean>();
    const [includeSenderEmail, setIncludeSenderEmail] = useState(false);
    const [includeSenderUserName, setIncludeSenderUserName] = useState(false);
    const [notSelectedRecipients, setNotSelectedRecipients] = useState<MBSRecipient[]>([]);
    const [selectedRecipients, setSelectedRecipients] = useState<MBSRecipient[]>([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [mobileAllChecked, setMobileAllChecked] = useState(user?.Role === 3 ? false : true);
    const [emailAllChecked, setEmailAllChecked] = useState(true);
    const [notificationAllChecked, setNotificationAllChecked] = useState(true);
    const [criticalAlertIsChecked, setCriticalAlertIsChecked] = useState(false);
    const [geoLocationIsChecked, setGeoLocationIsChecked] = useState(false);
    const [templateEnabled, setTemplateEnabled] = useState(false);

    const [messageBody, setMessageBody] = useState("");
    const [messageTitle, setMessageTitle] = useState("");
    const [messageQuestion, setMessageQuestion] = useState("");
    const [responseAlternatives, setResponseAlternatives] = useState<Array<{ id: string, value: string }>>([]);
    const [templateName, setTemplateName] = useState(""); // New state for template name
    const [messageTitleError, setMessageTitleError] = useState("");
    const [messageBodyError, setMessageBodyError] = useState("");
    const [messageQuestionError, setMessageQuestionError] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<SendOutBatchTemplate | CustomAlertResponse>();
    const [templateDescription, setTemplateDescription] = useState("");
    const [questionRequired, setQuestionRequired] = useState(false);

    const [optionSets, setOptionSets] = useState<OptionSetViewModel[]>([]);
    const [savedTemplateId, setSavedTemplateId] = useState<number | null>(templateId);
    const [hasChanges, setHasChanges] = useState(false);

    const templates: SendOutBatchTemplate[] = [
        {
            id: 1,
            title: translations.onewaycommunication,
            description: translations.onewaycomdescription,
            replyRequired: false,
            questionIncluded: false
        },
        {
            id: 2,
            title: translations.twowaycommunication,
            description: translations.twowaycomdescription,
            alternatives: [translations.yes, translations.no],
            replyRequired: true,
            questionIncluded: true
        }
    ];

    const updateAlertTemplateAsync = (dto: UpdateAlertTemplateDTO): Promise<UpdateAlertTemplateDTO> => {
        return new Promise((resolve, reject) => {
            updateAlertTemplate(dto, {
                onSuccess: resolve,
                onError: reject,
            });
        });
    };

    const createAlertTemplateAsync = (dto: CreateAlertTemplateDTO): Promise<AlertTemplateDTO> => {
        return new Promise((resolve, reject) => {
            createAlertTemplate(dto, {
                onSuccess: resolve,
                onError: reject,
            });
        });
    };

    const { customer } = useCustomer();
    //const { data: customTemplates } = useGetAlertTemplates(customer?.Id);

    const {
        data: dataUsers
    } = useGetUsers(customer.Id);

    const {
        data: dataContacts
    } = useGetContacts(customer.Id);

    const {
        data: dataContactGroups
    } = useGetContactGroups(customer.Id);

    const {
        data: dataCustomerContactOptionSets
    } = useGetCustomerContactOptionSets(customer.Id);

    const { data: getLoadedTemplate } = useGetAlertTemplate(savedTemplateId!, {
        enabled: !!savedTemplateId,
    });

    // Load template if templateId is present
    useEffect(() => {
        //console.log("try to load");
       
        if (hasInitialized.current) return;

        if (
            templateId &&
            getLoadedTemplate &&
            dataUsers &&
            dataContacts &&
            dataContactGroups
        ) {
        //    console.log("OKAY! Load!");
            hasInitialized.current = true;

            const loadedTemplate = getLoadedTemplate;
            isInitialLoad.current = true;

            setSelectedTemplateId(`custom-${templateId}`);
            // setSelectedTemplate(loadedTemplate.);
            setQuestionRequired(loadedTemplate.ResponseRequired);
            setTemplateName(loadedTemplate.Name);
            setMessageTitle(loadedTemplate.Subject);
            setMessageBody(loadedTemplate.Body);
            setMessageQuestion(loadedTemplate.Question);
            setEmailAllChecked(loadedTemplate.EmailEnabled);
            setMobileAllChecked(loadedTemplate.SMSEnabled);
            setNotificationAllChecked(loadedTemplate.PushNotificationEnabled);
            setCriticalAlertIsChecked(loadedTemplate.CriticalAlertEnabled);
            setGeoLocationIsChecked(loadedTemplate.GeoLocationEnabled);
            setEnableEventflow(loadedTemplate.EventFlowEnabled);
            setTemplateEnabled(loadedTemplate.Enabled);
            setIncludeSenderEmail(loadedTemplate.IncludeSenderEmailInBody);
            setIncludeSenderUserName(loadedTemplate.IncludeSenderUserNameInBody);

            if (loadedTemplate.ResponseAlternatives && loadedTemplate.ResponseAlternatives.length > 0) {
                const alternatives = loadedTemplate.ResponseAlternatives.map((alt, index) => ({
                    id: `alt-${index}`,
                    value: alt
                }));

                setResponseAlternatives(alternatives);
            }

            const updatedUsers = createUserRecipients(dataUsers);
            const updatedContacts = createContactRecipients(dataContacts);
            const updatedGroups = createContactGroupRecipients(dataContactGroups);
            const allRecipients = [...updatedGroups, ...updatedUsers, ...updatedContacts];

            const recipientLookup: Record<string, boolean> = {};
            (loadedTemplate.Recipients || []).forEach(rec => {
                if (rec.CustomerUserId) recipientLookup[`user:${rec.CustomerUserId.toLowerCase()}`] = true;
                if (rec.ContactId != null) recipientLookup[`contact:${rec.ContactId}`] = true;
                if (rec.ContactGroupId != null) recipientLookup[`group:${rec.ContactGroupId}`] = true;
            });

            const updatedRecipientList = allRecipients.map(item => {
                let key = '';
                if (item.type === 'internaluser') key = `user:${item.id?.toString().toLowerCase()}`;
                else if (item.type === 'internalcontact') key = `contact:${item.id}`;
                else if (item.type === 'internalgroup') key = `group:${item.id}`;

                return {
                    ...item,
                    isAdded: !!recipientLookup[key],
                    emailChecked: loadedTemplate.EmailEnabled,
                    mobileChecked: loadedTemplate.SMSEnabled,
                    notificationChecked: loadedTemplate.PushNotificationEnabled,
                };
            });

            const typeId = loadedTemplate.ResponseRequired ? 2 : 1;
            setSelectedTemplateId(`default-${typeId}`);
            const matchingTemplate = templates.find(t => t.id === typeId);
            if (matchingTemplate) {
                setSelectedTemplate(matchingTemplate);
            }

            setNotSelectedRecipients(updatedRecipientList);
            setTemplateName(loadedTemplate.Name); // include any other one-time loads here
            setLoaded(true);
        //    console.log('loaded Klart');

            setTimeout(() => {
                isInitialLoad.current = false;
                }, 1000);
              //  console.log('after timeout');
       }
    }, [templateId, getLoadedTemplate, dataUsers, dataContacts, dataContactGroups]);


    useEffect(() => {
        if (!templateId) {
            // No template to load, start tracking changes immediately
            isInitialLoad.current = false;
        }
    }, [templateId]);

    const createOptionSetViewModels = (data: OptionSetModel[]): OptionSetViewModel[] => {
        const mappedData = data.map(p => ({
            name: p.Name,
            id: p.ID,
            options: p.Options.map(q => ({
                id: q.ID,
                value: q.Value
            }))
        }));

        mappedData.sort((a, b) => a?.name?.localeCompare(b.name));
        return mappedData;
    };

    const createUserRecipients = (data: MurphyUser[]): MBSRecipient[] => {
        const mappedUsers = data.map(
            p =>
                ({
                    uniqueKey: `${"White"}:${p.UserId}`,
                    id: p.UserId,
                    name: p.Username,
                    email: p.Email,
                    mobile: p.Phonenumber,
                    deviceToken: p.DeviceToken,
                    type: "internaluser",
                    isGroup: false,
                    mobileChecked: !!p.Phonenumber, // true if MobileNr exists and is not falsy
                    emailChecked: !!p.Email,     // true if Email exists and is not falsy
                    notificationChecked: !!p.DeviceToken, // true if DeviceToken exists and is not falsy
                    checked: true
                }) as MBSRecipient
        );

        return mappedUsers;
    };

    const createContactRecipients = (data: ContactModel[]): MBSRecipient[] => {
        const mappedContacts = data.map(
            p =>
                ({
                    uniqueKey: `${"internalcontact"}:${p.ID}`,
                    id: p.ID,
                    name: p.Name,
                    email: p.Email,
                    mobile: p.MobileNr,
                    deviceToken: p.DeviceToken,
                    type: "internalcontact",
                    mobileChecked: !!p.MobileNr, // true if MobileNr exists and is not falsy
                    emailChecked: !!p.Email,     // true if Email exists and is not falsy
                    notificationChecked: !!p.DeviceToken, // true if DeviceToken exists and is not falsy
                    checked: true,
                    isGroup: false,
                    selectedOptions: p.SelectedOptions
                        ? p.SelectedOptions.map(
                            q =>
                                ({
                                    id: q.ID,
                                    value: q.Value,
                                    optionSetId: q.OptionSetId,
                                    optionSetName: q.OptionSetName
                                }) as SelectedOptionSetViewModel
                        )
                        : []
                }) as MBSRecipient
        );
        return mappedContacts;
    };

    const createContactGroupRecipients = (data: ContactGroupModel[]): MBSRecipient[] => {
        const mappedGroups = data
            .map(p => {
                let mappedContacts: MBSGroupMember[] = [];
                let mappedUsers: MBSGroupMember[] = [];

                if (p.ContactGroupContacts) {
                    mappedContacts = p.ContactGroupContacts.map(q => ({
                        name: q.Name,
                        id: q.ID.toString(),
                        email: q.Email,
                        mobile: q.MobileNr,
                        deviceToken: q.DeviceToken,
                        mobileChecked: !!q.MobileNr,
                        emailChecked: !!q.Email,
                        notificationChecked: !!q.DeviceToken,
                        type: "contact"
                    }));
                }

                if (p.ContactGroupUsers) {
                    mappedUsers = p.ContactGroupUsers.map(q => ({
                        name: q.UserName,
                        id: q.ID.toString(),
                        customeruserid: q.CustomerUserId?.toUpperCase(),
                        email: q.Email,
                        mobile: q.MobilePhone,
                        deviceToken: q.DeviceToken,
                        mobileChecked: !!q.MobilePhone,
                        emailChecked: !!q.Email,
                        notificationChecked: !!q.DeviceToken,
                        type: "user"
                    }));
                }

                const allGroupMembers = [...mappedUsers, ...mappedContacts];

                if (allGroupMembers.length === 0) return null;

                return {
                    uniqueKey: `internalgroup:${p.ID}`,
                    id: p.ID,
                    name: p.Name,
                    email: `${p.Name} (${translations.group})`,
                    mobile: `${p.Name} (${translations.group})`,
                    deviceToken: `${p.Name} (${translations.group})`,
                    type: "internalgroup",
                    mobileChecked: true,
                    emailChecked: true,
                    notificationChecked: true,
                    checked: true,
                    isGroup: true,
                    groupMembers: allGroupMembers
                } as MBSRecipient;
            })
            .filter(Boolean); // removes null entries
        return mappedGroups;
    };

    const onSelectTemplate = (templateId: number) => {
        const newId = `default-${templateId}`;
        setSelectedTemplateId(newId);

        const selectedTemplate = templates.find(p => p.id === templateId);
        if (!selectedTemplate) return;

        setSelectedTemplate(selectedTemplate);
        setQuestionRequired(selectedTemplate.replyRequired);


        // Nollställ kanalval
        setEmailAllChecked(false);
        setMobileAllChecked(false);
        setNotificationAllChecked(false);
        setCriticalAlertIsChecked(false);

        // Nollställ kanaler för varje mottagare
        const newNotSelectedRecipients = notSelectedRecipients.map(item => ({
            ...item,
            mobileChecked: false,
            emailChecked: false,
            notificationChecked: false,
            isAdded: false
        }));

        setNotSelectedRecipients(newNotSelectedRecipients);

        // Rensa/bestäm meddelandeinnehåll
        setMessageTitle(selectedTemplate?.title || "");
        setMessageBody(selectedTemplate?.body || "");
        setMessageQuestion(selectedTemplate?.question || "");
    };


    useEffect(() => {
        if (stepsEnabled && selectedTemplateId !== null) {
            setMessageTemplateIsFlashing(false)
        }
    }, [selectedTemplateId, stepsEnabled]);

    function NoUsers() {
        return <p>{translations.nousersselected}</p>;
    }

    function NoTemplate() {
        return <p>{translations.notemplateselected}</p>;
    }

    function ErrorMessage() {
        return <p>{error}</p>;
    }

    const onFail = errorData => {
        if (errorData.statusText) {
            setError(errorData.statusText);
        } else {
            setError("Error");
        }
    };

    const onMessageBodyChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setMessageBodyError(translations.mandatory);
        } else {
            setMessageBodyError("");
        }
        setMessageBody(event.target.value);
    };

    const onMessageTitleChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setMessageTitleError(translations.mandatory);
        } else {
            setMessageTitleError("");
        }
        setMessageTitle(event.target.value);
    };

    const onMessageQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setMessageQuestionError(translations.mandatory);
        } else {
            setMessageQuestionError("");
        }
        setMessageQuestion(event.target.value);
    };

    const addResponseAlternative = () => {
        const newId = `alt-${Date.now()}`;
        setResponseAlternatives(prev => [...prev, { id: newId, value: '' }]);
    };

    const removeResponseAlternative = (id: string) => {
        setResponseAlternatives(prev => prev.filter(alt => alt.id !== id));
    };

    const updateResponseAlternative = (id: string, value: string) => {
        setResponseAlternatives(prev =>
            prev.map(alt => alt.id === id ? { ...alt, value } : alt)
        );
    };

    const addRecipient = (recipient: MBSRecipient): void => {
        const newNotSelectedRecipients = notSelectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                isAdded: recipient.uniqueKey === item.uniqueKey ? !item.isAdded : item.isAdded
            };
            return updatedItem;
        });
        setNotSelectedRecipients(newNotSelectedRecipients);
    };

    const removeRecipient = (recipient: MBSRecipient): void => {
        const newRecipient = { ...recipient, isAdded: false };
        const notSelectedRecipientsCopy = [...notSelectedRecipients, newRecipient];
        setNotSelectedRecipients(notSelectedRecipientsCopy);
    };

    const onCheckCriticalAlert = () => {
        setCriticalAlertIsChecked(!criticalAlertIsChecked);
    };

    useEffect(() => {
        if (dataContactGroups) {
            const contactGroupRecipients = createContactGroupRecipients(dataContactGroups);
            setContactGroups(contactGroupRecipients);
        }
        return () => {
            setContactGroups([]);
        };
    }, [dataContactGroups]);

    useEffect(() => {
        if (dataUsers) {
            const userRecipients = createUserRecipients(dataUsers);
            setUsers(userRecipients);
        }
        return () => {
            setUsers([]);
        };
    }, [dataUsers]);

    useEffect(() => {
        if (dataContacts) {
            const contactRecipients = createContactRecipients(dataContacts);
            setContacts(contactRecipients);
        }
        return () => {
            setContacts([]);
        };
    }, [dataContacts]);

    useEffect(() => {
        if (dataCustomerContactOptionSets) {
            const optionSetViewModels = createOptionSetViewModels(dataCustomerContactOptionSets);
            setOptionSets(optionSetViewModels);
        }
    }, [dataCustomerContactOptionSets]);

    useEffect(() => {
        if (!templateId) {
            const allRec = [
                ...contactGroups.sort((a, b) => a?.name?.localeCompare(b.name)),
                ...users.sort((a, b) => a?.name?.localeCompare(b.name)),
                ...contacts.sort((a, b) => a?.name?.localeCompare(b.name))
            ];
            setNotSelectedRecipients([...allRec]);
        }
    }, [users, contactGroups, contacts]);

    useEffect(() => {
        if (error) {
            const modalConf: ModalConfiguration = {
                title: "Error",
                body: <ErrorMessage />,
                buttonText: "Ok",
                type: "error",
                okCallback: () => {
                    setError(null);
                }
            };

            openModal(modalConf);
        }
    }, [error]);

    useEffect(() => {
        if (isInitialLoad.current) {
           // console.log('still loading dont save!');
            return;
        }
       // console.log('Saving time!');
        setHasChanges(true); // Set to true on any state change; you can make it more specific if needed
    }, [geoLocationIsChecked, EnableEventflowIsChecked, includeSenderEmail, includeSenderUserName, mobileAllChecked, questionRequired, messageQuestion, responseAlternatives, messageBody, messageTitle, templateName, templateDescription, emailAllChecked, notificationAllChecked, criticalAlertIsChecked, notSelectedRecipients]);

    const onCheckAllMobile = () => {
        const newSelectedRecipients = selectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                mobileChecked: !mobileAllChecked
            }
            return updatedItem
        })
        setSelectedRecipients(newSelectedRecipients)

        const newNotSelectedRecipients = notSelectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                mobileChecked: !mobileAllChecked
            }
            return updatedItem
        })
        setNotSelectedRecipients(newNotSelectedRecipients)

        setMobileAllChecked(!mobileAllChecked)
    }

    const onCheckAllEmail = () => {
        const newSelectedRecipients = selectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                emailChecked: !emailAllChecked
            }
            return updatedItem
        })
        setSelectedRecipients(newSelectedRecipients)

        const newNotSelectedRecipients = notSelectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                emailChecked: !emailAllChecked
            }
            return updatedItem
        })
        setNotSelectedRecipients(newNotSelectedRecipients)
        setEmailAllChecked(!emailAllChecked)
    }

    const onCheckAllNotification = () => {
        const newSelectedRecipients = selectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                notificationChecked: !notificationAllChecked
            }
            return updatedItem
        })
        setSelectedRecipients(newSelectedRecipients)

        const newNotSelectedRecipients = notSelectedRecipients.map(item => {
            const updatedItem = {
                ...item,
                notificationChecked: !notificationAllChecked
            }
            return updatedItem
        })
        setNotSelectedRecipients(newNotSelectedRecipients)
        setNotificationAllChecked(!notificationAllChecked)
    }

    const onClickRecipient = (recipient: MBSRecipient) => {
        const type = recipient.type
        const clickedId = recipient.id

        if (type === "internalgroup") {
            const contactGroupToDisplay = contactGroups.filter(p => p.id == clickedId)[0]
            const contactGroupTitle = `${translations.contactgroup} - ${contactGroupToDisplay.name}`
            const mappedContacts = contactGroupToDisplay.groupMembers.map(p => {
                return {
                    id: p.id,
                    name: p.name,
                    email: p.email,
                    mobile: p.mobile,
                    deviceToken: p.deviceToken,
                    type: p.type
                } as ContactListModel
            })

            const contactList = (
                <div>
                    <h3>{translations.groupmembers}</h3>
                    <ContactList
                        translations={translations}
                        contacts={mappedContacts}
                        showEdit={false}
                        showDelete={false}
                        showAdd={false}
                    />
                </div>
            )

            const modalConf: ModalConfiguration = {
                title: contactGroupTitle,
                body: contactList,
                buttonText: "Ok",
                type: "info",
                position: 'center',
                centerContent: false
            }

            openModal(modalConf)
        } else if (type === "internaluser") {
            const userToDisplay = users.filter(p => p.id == clickedId)[0]
            const userTitle = `${translations.user} - ${userToDisplay.name}`

            const modalConf: ModalConfiguration = {
                title: userTitle,
                body: (
                    <EditContact
                        translations={translations}
                        customerId={customer.Id}
                        contactId={userToDisplay.id}
                        nameInit={userToDisplay.name}
                        emailInit={userToDisplay.email}
                        mobileInit={userToDisplay.mobile}
                        displayOnly
                    />
                ),
                hideButton: true,
                position: 'center',
                type: "info"
            }

            openModal(modalConf)
        } else if (type === "internalcontact") {
            const contactToDisplay = contacts.filter(p => p.id == clickedId)[0]
            const contactTitle = `${translations.contact} - ${contactToDisplay.name}`

            const modalConf: ModalConfiguration = {
                title: contactTitle,
                body: (
                    <EditContact
                        translations={translations}
                        customerId={customer.Id}
                        contactId={contactToDisplay.id}
                        nameInit={contactToDisplay.name}
                        emailInit={contactToDisplay.email}
                        mobileInit={contactToDisplay.mobile}
                        selectedOptions={contactToDisplay.selectedOptions}
                        optionSets={optionSets}
                        displayOnly={true}
                    />
                ),
                hideButton: true,
                position: 'center',
                type: "info"
            }
            openModal(modalConf)
        }
    }


    if (isLoading) {
        return (
            <ComponentWrapper active={2} subMenuActive={1}>
                <div className='spinner-container-global-center'>
                    <Spinner />
                </div>
            </ComponentWrapper>
        );
    }

    const tabs = [
        {
            id: 1,
            displayText: translations.typ || "Type",
            customClass: "tab-transparent-panel-tab",
            customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
        },
        {
            id: 2,
            displayText: translations.channels || "Channel(s)",
            customClass: "tab-transparent-panel-tab",
            customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
        },
        {
            id: 3,
            displayText: translations.message || "Message",
            customClass: "tab-transparent-panel-tab",
            customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
        },
        {
            id: 4,
            displayText: translations.recipients || "Recipients",
            customClass: "tab-transparent-panel-tab",
            customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
        },
        {
            id: 5,
            displayText: translations.confirmation || "Confirmation",
            customClass: "tab-transparent-panel-tab",
            customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
        }
    ];

    const onClickTab = (tabId: number) => {
        if (tabId === tabs.length) {
            setShowValidationErrors(!canProceedToSubmit);
        } else {
            setShowValidationErrors(false);
        }
        setSelectedTabId(tabId);
    };

    // Add before the onNext function
    const saveTemplate = async () => {
        console.log('save');
        if (isSaving) return;
        //  console.log('set saving True');
        setIsSaving(true);

        try {
            const responseAlternativeValues = responseAlternatives
                .map(alt => alt.value.trim())
                .filter(value => value.length > 0);

            const recipientsDTO: AlertTemplateRecipientDTO[] = notSelectedRecipients
                .filter(r => r.isAdded)
                .map(r => {
                    const dto: AlertTemplateRecipientDTO = {};
                    if (r.type === "internaluser") dto.CustomerUserId = r.id.toString();
                    if (r.type === "internalcontact") dto.ContactId = r.id;
                    if (r.type === "internalgroup") dto.ContactGroupId = r.id;
                    return dto;
                });

            const dtoBase = {
                Name: templateName,
                Description: templateDescription,
                Subject: messageTitle,
                Body: messageBody,
                Question: messageQuestion,
                ResponseRequired: questionRequired,
                ResponseAlternatives: responseAlternativeValues,
                CriticalAlertEnabled: criticalAlertIsChecked,
                GeoLocationEnabled: geoLocationIsChecked,
                CustomerId: customer.Id,
                SMSEnabled: mobileAllChecked,
                EmailEnabled: emailAllChecked,
                PushNotificationEnabled: notificationAllChecked,
                Recipients: recipientsDTO,
                EventFlowEnabled: EnableEventflowIsChecked,
                Enabled: templateEnabled,
                IncludeSenderEmailInBody: includeSenderEmail,
                IncludeSenderUserNameInBody: includeSenderUserName,
            };

            try {
                let updatedTemplate: UpdateAlertTemplateDTO;
                if (savedTemplateId) {
                    const updateDto: UpdateAlertTemplateDTO = {
                        ID: savedTemplateId,
                        ...dtoBase,
                    };
                    console.log('update');
                    updatedTemplate = await updateAlertTemplateAsync(updateDto);
                } else {
                    console.log('create');
                    updatedTemplate = await createAlertTemplateAsync(dtoBase); // <-- FIXED
                    setSavedTemplateId(updatedTemplate.ID); // use updatedTemplate
                    navigate(`?templateId=${updatedTemplate.ID}`, { replace: true });
                }

                setHasChanges(false);
                hasInitialized.current = false;
                setLoaded(false); // Force re-run of `useEffect(...)` that loads `getLoadedTemplate`
                //  console.log('hasChanges,loaded current all false');
            } catch (err) {
                setError("Could not save template " + err);
            }
        } catch (err) {
            setError("Could not save template " + err);
        } finally {
            setIsSaving(false);
            //console.log('set saving false');
        }
    }

    const toggleTemplateEnabled = async () => {
        const newEnabledStatus = !templateEnabled;
        setTemplateEnabled(newEnabledStatus);

        if (savedTemplateId) {
            try {
                const responseAlternativeValues = responseAlternatives
                    .map(alt => alt.value.trim())
                    .filter(value => value.length > 0);

                const recipientsDTO: AlertTemplateRecipientDTO[] = notSelectedRecipients
                    .filter(r => r.isAdded)
                    .map(r => {
                        const dto: AlertTemplateRecipientDTO = {};
                        if (r.type === "internaluser") dto.CustomerUserId = r.id.toString();
                        if (r.type === "internalcontact") dto.ContactId = r.id;
                        if (r.type === "internalgroup") dto.ContactGroupId = r.id;
                        return dto;
                    });

                const updateDto: UpdateAlertTemplateDTO = {
                    ID: savedTemplateId,
                    Name: templateName,
                    Description: templateDescription,
                    Subject: messageTitle,
                    Body: messageBody,
                    Question: messageQuestion,
                    ResponseRequired: questionRequired,
                    ResponseAlternatives: responseAlternativeValues,
                    CriticalAlertEnabled: criticalAlertIsChecked,
                    GeoLocationEnabled: geoLocationIsChecked,
                    CustomerId: customer.Id,
                    SMSEnabled: mobileAllChecked,
                    EmailEnabled: emailAllChecked,
                    PushNotificationEnabled: notificationAllChecked,
                    Recipients: recipientsDTO,
                    EventFlowEnabled: EnableEventflowIsChecked,
                    Enabled: newEnabledStatus,
                    IncludeSenderEmailInBody: includeSenderEmail,
                    IncludeSenderUserNameInBody: includeSenderUserName,
                };

                await updateAlertTemplateAsync(updateDto);
            } catch (err) {
                setTemplateEnabled(!newEnabledStatus);
                setError("Could not update template status: " + err);
            }
        }
    };

    const onBack = () => {
        if (hasChanges) {
            saveTemplate();
        }

        setSelectedTabId(selectedTabId - 1);
    };

    const onNext = () => {
        const isCustomTemplate = selectedTemplate && ![1, 2].includes(selectedTemplate.id);
        const isLastTab = selectedTabId === tabs.length;

        if (isLastTab) {
            if (!canProceedToSubmit) {
                setShowValidationErrors(true);
                return;
            }

            showConfirmModal();
        } else {
            let nextTabId = selectedTabId + 1;

            if (
                isCustomTemplate &&
                !selectedTemplate.startFromBeginning
            ) {
                if (
                    !(mobileAllChecked || emailAllChecked || notificationAllChecked)
                ) {
                    nextTabId = 2;
                } else if (
                    !messageBody ||
                    (selectedTemplate?.ResponseRequired && !messageQuestion)
                ) {
                    nextTabId = 3;
                } else if (notSelectedRecipients?.filter(item => item.isAdded).length === 0) {
                    nextTabId = 4;
                } else {
                    nextTabId = tabs.length;
                }
            }

            if (hasChanges) {
                saveTemplate();
            }

            setShowValidationErrors(nextTabId === tabs.length && !canProceedToSubmit);
            setSelectedTabId(nextTabId);
        }
    };

    const validRecipients = notSelectedRecipients.filter(
        p =>
            p.isAdded &&
            ((p.mobileChecked && p.mobile) ||
                (p.emailChecked && p.email) ||
                (p.notificationChecked && p.deviceToken))
    );

    const hasRecipients = validRecipients.length > 0;
    const hasMessageTitle = !!messageTitle?.trim();
    const hasMessageBody = !!messageBody?.trim();
    const hasAnyChannel = mobileAllChecked || emailAllChecked || notificationAllChecked;

    const hasTemplateName = !!templateName?.trim();
    const hasTemplateDescription = !!templateDescription?.trim();
    const canProceedToSubmit = hasTemplateName && hasTemplateDescription && hasRecipients && hasMessageTitle && hasMessageBody && hasAnyChannel && (!questionRequired || !!messageQuestion.trim());

    const renderTabBody = () => {
        switch (selectedTabId) {
            case 1:
                return (
                    <AlertTemplateBasicInfo
                        templates={templates}
                        onSelectTemplate={onSelectTemplate}
                        selectedId={selectedTemplateId}
                        translations={translations}
                        name={templateName}
                        description={templateDescription}
                        messageTemplateIsFlashing={messageTemplateIsFlashing}
                        handleNameChange={(e) => setTemplateName(e.target.value)}
                        handleDescriptionChange={(e) => setTemplateDescription(e.target.value)}
                        onEnableEventflow={setEnableEventflow}
                        EnableEventflowIsChecked={EnableEventflowIsChecked}
                        includeSenderEmail={includeSenderEmail}
                        includeSenderUserName={includeSenderUserName}
                        onChangeIncludeSenderEmail={setIncludeSenderEmail}
                        onChangeIncludeSenderUserName={setIncludeSenderUserName}
                    />
                );
            case 2:
                return (
                    <ChannelSelection
                        onCheckAllMobile={onCheckAllMobile}
                        onCheckAllEmail={onCheckAllEmail}
                        onCheckAllNotification={onCheckAllNotification}
                        onCheckCriticalAlert={onCheckCriticalAlert}
                        checkAllMobileChecked={mobileAllChecked}
                        checkAllEmailChecked={emailAllChecked}
                        checkAllNotificationChecked={notificationAllChecked}
                        criticalAlertIsChecked={criticalAlertIsChecked}
                        smsDisabled={user?.Role === Roles.Trial || enabledFeatures.mbs.disableSMS === "true"}
                        translations={translations}
                        notificationDisabled={enabledFeatures.mbs.disableNotification === "true"}
                        emailDisabled={enabledFeatures.mbs.disableEmail === "true"}
                    />
                );
            case 3:
                return (
                    <MBSMessageEditor
                        translations={translations}
                        handleMessageBodyChange={onMessageBodyChanged}
                        handleMessageTitleChange={onMessageTitleChanged}
                        handleQuestionChange={onMessageQuestionChange}
                        messageTitleError={messageTitleError}
                        messageBodyError={messageBodyError}
                        messageQuestionError={messageQuestionError}
                        message={messageBody}
                        title={messageTitle}
                        question={messageQuestion}
                        showQuestionEditor={questionRequired}
                        rows={5}
                        cols={20}
                        showNoOfSms={mobileAllChecked}
                        responseAlternatives={responseAlternatives}
                        onAddResponseAlternative={addResponseAlternative}
                        onRemoveResponseAlternative={removeResponseAlternative}
                        onUpdateResponseAlternative={updateResponseAlternative}
                        hideOptions={false}
                    />
                );
            case 4:
                return (
                    <MBSChooseRecipients
                        translations={translations}
                        recipients={notSelectedRecipients}
                        contactGroups={contactGroups}
                        addRecipient={addRecipient}
                        clickRecipient={onClickRecipient}
                    />
                );
            case 5:
                const flatRecipients: MBSRecipient[] = notSelectedRecipients
                    .filter(p => p.isAdded)
                    .reduce((acc, p) => {
                        if (p.isGroup && p.groupMembers?.length) {
                            const mappedMembers: MBSRecipient[] = p.groupMembers.map(member => ({
                                ...member,
                                name: member.name,
                                id: member.id,
                                email: member.email,
                                mobile: member.mobile,
                                deviceToken: member.deviceToken,
                                mobileChecked: member.mobileChecked,
                                emailChecked: member.emailChecked,
                                notificationChecked: member.notificationChecked,
                                uniqueKey: `${member.type}:${member.id}`,
                                type: member.type,
                                isGroup: false,
                                checked: true,         // <- required by MBSRecipient
                                isAdded: true,         // <- you probably want them included
                            }));
                            return acc.concat(mappedMembers);
                        }

                        return acc.concat({
                            ...p,
                            checked: true,           // ensure required fields
                            isAdded: true,
                        });
                    }, [] as MBSRecipient[])
                    .filter(p =>
                        (p.mobileChecked && p.mobile) ||
                        (p.emailChecked && p.email) ||
                        (p.notificationChecked && p.deviceToken)
                    );

                return (
                    <MBSConfirmation
                        translations={translations}
                        messageTitle={messageTitle}
                        messageBody={messageBody}
                        messageQuestion={messageQuestion}
                        onClickRecipient={onClickRecipient}
                        questionRequired={questionRequired}
                        mobileChecked={mobileAllChecked}
                        notificationChecked={notificationAllChecked}
                        emailChecked={emailAllChecked}
                        recipients={flatRecipients}
                        criticalAlert={criticalAlertIsChecked}
                        showValidationErrors={showValidationErrors}
                    />
                );
        }
    };

    return (
        <ComponentWrapper active={8} subMenuActive={5}>
            <div className="pbs-wrapper">
                <h1 className="heading">{translations.createalert || "Create new alert"}</h1>
                <div style={{ width: "80rem", backgroundColor: "#E0E1DD", height: "5px" }}>
                    <div
                        style={{
                            backgroundColor: "#00A098",
                            width: `${(selectedTabId / tabs.length) * 80}rem`,
                            height: "5px",
                        }}
                    />
                </div>
                <div className="container">
                    <TabPanel
                        tabs={tabs}
                        onClickTab={onClickTab}
                        clickable={true}
                        selectedTabId={selectedTabId}
                        customClass="tab-solid-panel-tms"
                    />
                    <div style={{ height: "50rem" }}>
                        {renderTabBody()}
                        <div style={{ flexDirection: "row", marginTop: 30, display: "flex", paddingLeft: 20 }}>
                            {selectedTabId > 1 && (
                                <Button
                                    buttonStyle="btn--primary"
                                    buttonSize="btn--medium"
                                    onClick={onBack}
                                >
                                    {translations.back || "Back"}
                                </Button>
                            )}
                            <div style={{ marginLeft: 20 }}>
                                <Button
                                    buttonStyle="btn--primary"
                                    buttonSize="btn--medium"
                                    onClick={selectedTabId === tabs.length ? toggleTemplateEnabled : onNext}
                                >
                                    {selectedTabId === tabs.length
                                        ? (templateEnabled
                                            ? (translations.disableTemplate || "Disable template")
                                            : (translations.enableTemplate || "Enable template"))
                                        : (translations.nextBox || "Next")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ComponentWrapper>
    );
};

export default CreateAlertTemplate;